import React, { useCallback, useState, useEffect, useMemo } from "react";
import * as FaIcons from "react-icons/fa";
import * as AiIcons from "react-icons/ai";
import * as IoIcons from "react-icons/io";
import * as BsIcons from "react-icons/bs";
import * as MdIcons from "react-icons/md";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import * as actionType from "../../constants/actionTypes";
import "./Navbar.css";
import { useDispatch } from "react-redux";
import { tokenExpired } from "../token";
import UNAUTHORIZED from "../UNAUTHORIZED";
import { STATEMENT_USERS } from "../Efada/EfadaHistory";

// ── Menu ─────────────────────────────────────────────────────────────────────
// Every sidebar entry, in sections. `show(u)` decides who sees it (u = the
// logged-in user: { username, role }); entries without `show` are open to
// everyone. A group (with `children`) is shown when its own `show` passes and
// at least one child is visible. `external` links open in a new tab.
const roleIn = (...roles) => (u) => roles.includes(u.role);
const userIn = (...names) => (u) => names.includes(u.username);

const MENU = [
  {
    section: "General",
    items: [
      { label: "Main", to: "/main", icon: AiIcons.AiFillHome, end: true },
      { label: "Overview", to: "/main/overview", icon: IoIcons.IoIosPaper, show: roleIn("admin") },
      { label: "Table", to: "/main/table", icon: BsIcons.BsTable, show: roleIn("admin") },
      { label: "Global Actions", to: "/main/actions", icon: IoIcons.IoMdPeople, show: roleIn("admin") },
    ],
  },
  {
    section: "Administration",
    items: [
      { label: "Manage Users", to: "/main/add_user", icon: FaIcons.FaUser, show: userIn("admin") },
      {
        label: "Manage Schools",
        to: "/main/schools",
        icon: FaIcons.FaUniversity,
        show: userIn("admin", "abdelrhman"),
      },
      {
        label: "Enrollment Letters",
        to: "/main/enrollment_letters",
        icon: FaIcons.FaFileUpload,
        show: userIn("acountant-1", "admin", "asmaa", "Yara", "abdelrhman", "alaa", "agent", "Agent", "agent5", "agent-10"),
      },
      { label: "QR Codes", to: "/main/qrcodes", icon: FaIcons.FaQrcode, show: userIn("admin", "asmaa", "abdelrhman") },
      {
        label: "Send Score Submissions",
        to: "/main/send_score_submissions",
        icon: FaIcons.FaClipboardCheck,
        show: (u) => userIn("asmaa", "abdelrhman")(u) || u.role === "super-admin",
      },
    ],
  },
  {
    section: "Workflow",
    items: [
      {
        label: "Auditing",
        icon: FaIcons.FaEdit,
        show: roleIn("user", "admin"),
        children: [
          { label: "New", to: "/main/auditing/new" },
          { label: "Pending", to: "/main/auditing/pending" },
          { label: "Request Edit", to: "/main/auditing/request_edit", show: userIn("asmaa", "abdelrhman", "alaa") },
        ],
      },
      {
        label: "Invoice",
        icon: FaIcons.FaFileInvoiceDollar,
        show: roleIn("user", "invoice", "admin"),
        children: [
          { label: "New", to: "/main/invoice/new" },
          { label: "Invoice Wait", to: "/main/invoice/wait" },
          { label: "Another Invoice", to: "/main/invoice/another_invoice", show: roleIn("invoice", "admin") },
        ],
      },
      {
        label: "Print",
        icon: MdIcons.MdPrint,
        show: roleIn("user", "admin"),
        children: [
          { label: "Print", to: "/main/print/new" },
          { label: "Print Validation", to: "/main/print/print_validation" },
        ],
      },
      {
        label: "Score Handler",
        icon: MdIcons.MdScore,
        show: roleIn("user", "admin"),
        children: [
          { label: "Score Validation", to: "/main/score/score_validation" },
          { label: "Score Request", to: "/main/score/new" },
          { label: "Score Pending", to: "/main/score/score_pending" },
        ],
      },
      {
        label: "MOE",
        icon: IoIcons.IoIosDocument,
        show: roleIn("user", "admin"),
        children: [
          { label: "MOE Validation", to: "/main/moe/moe_validation" },
          { label: "MOE Hold", to: "/main/moe/moe_hold" },
          { label: "Sending to MOE", to: "/main/moe/new" },
          { label: "Moe Pending", to: "/main/moe/moe_pending" },
          { label: "AATC Review", to: "/main/moe/aatc_review" },
        ],
      },
      {
        label: "Delivery",
        icon: MdIcons.MdDeliveryDining,
        show: roleIn("user", "admin"),
        children: [
          { label: "Delivery Hold", to: "/main/deliver/delivery_hold" },
          { label: "Delivery Onsite", to: "/main/deliver/deliver_onsite" },
          { label: "Aramex", to: "/main/deliver/aramex" },
        ],
      },
      {
        label: "MOHE",
        icon: FaIcons.FaLandmark,
        show: roleIn("user", "admin"),
        children: [
          { label: "MOHE Validation", to: "/main/mohe/mohe_validation" },
          { label: "Sending to MOHE", to: "/main/mohe/new" },
        ],
      },
      { label: "Done Students", to: "/main/done", icon: MdIcons.MdOutlineDoneOutline, show: roleIn("user", "admin") },
    ],
  },
  {
    section: "Students",
    items: [
      {
        label: "Top Achievers",
        icon: FaIcons.FaTrophy,
        children: [
          { label: "Top Achievers Guests", to: "/main/top_achievers_guests" },
          { label: "All Top Achievers", to: "/main/all_top_achievers", show: roleIn("user", "invoice", "admin") },
        ],
      },
      {
        // STATEMENT_USERS — admin / asmaa / abdelrhman.
        label: "Statements",
        icon: FaIcons.FaFileSignature,
        show: (u) => STATEMENT_USERS.includes(u.username),
        children: [
          { label: "الافادة (Statement)", to: "/main/efada" },
          { label: "Statement History", to: "/main/efada_history" },
        ],
      },
      {
        // Mirrors RUSH_REPORT_USERS in Server/src/components/student/student.routes.js.
        label: "Rush Students",
        to: "/main/rush_students",
        icon: MdIcons.MdOutlineLocalShipping,
        show: userIn("admin", "asmaa", "abdelrhman", "agent", "Agent", "agent5"),
      },
    ],
  },
  {
    section: "Forms",
    items: [
      { label: "Form Page", to: "/form", icon: MdIcons.MdAddBox, external: true },
      {
        label: "Transfers",
        icon: MdIcons.MdSwapHoriz,
        children: [
          { label: "Transfer Form", to: "/transfer", external: true },
          { label: "Transfer Submissions", to: "/main/transfer/submissions", show: roleIn("admin", "user") },
        ],
      },
    ],
  },
];

const visible = (item, u) => !item.show || item.show(u);

// The menu as this user sees it: hidden entries dropped, empty groups and
// sections dropped, and (when searching) only entries whose label matches.
function buildMenu(u, query) {
  const q = query.trim().toLowerCase();
  const matches = (label) => !q || label.toLowerCase().includes(q);
  return MENU.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => visible(item, u))
      .map((item) => {
        if (!item.children) return matches(item.label) ? item : null;
        const children = item.children.filter((c) => visible(c, u));
        const shown = matches(item.label) ? children : children.filter((c) => matches(c.label));
        return shown.length ? { ...item, children: shown } : null;
      })
      .filter(Boolean),
  })).filter((section) => section.items.length);
}

const isUnder = (pathname, to) => pathname === to || pathname.startsWith(`${to}/`);

// ── Pieces ───────────────────────────────────────────────────────────────────
const linkBase =
  "group flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm font-medium transition-colors";
const linkIdle = "text-slate-300 hover:bg-white/5 hover:text-white";
const linkActive = "bg-blue-600 text-white shadow-md shadow-blue-900/40";

function MenuLink({ item, onNavigate, nested }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      target={item.external ? "_blank" : undefined}
      onClick={onNavigate}
      className={({ isActive }) =>
        `${linkBase} ${nested ? "py-2 pl-3" : ""} ${isActive && !item.external ? linkActive : linkIdle}`
      }
    >
      {Icon && <Icon className="shrink-0 text-lg opacity-90" />}
      <span className="truncate">{item.label}</span>
      {item.external && <FaIcons.FaExternalLinkAlt className="ml-auto shrink-0 text-[10px] opacity-60" />}
    </NavLink>
  );
}

function MenuGroup({ item, open, onToggle, active, onNavigate }) {
  const Icon = item.icon;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`${linkBase} ${active ? "text-white bg-white/5" : linkIdle}`}
      >
        <Icon className={`shrink-0 text-lg ${active ? "text-blue-400" : "opacity-90"}`} />
        <span className="truncate">{item.label}</span>
        <FaIcons.FaChevronDown
          className={`ml-auto shrink-0 text-[10px] opacity-60 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div className="mt-1 mb-1 ml-5 pl-3 border-l border-white/10 space-y-0.5">
          {item.children.map((child) => (
            <MenuLink key={child.to} item={child} onNavigate={onNavigate} nested />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Layout ───────────────────────────────────────────────────────────────────
function Sidebar() {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("profile")));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [sidebar, setSidebar] = useState(false);
  const [query, setQuery] = useState("");
  // Open groups by label; the group holding the current page opens itself.
  const [openGroups, setOpenGroups] = useState({});

  const logout = useCallback(() => {
    dispatch({ type: actionType.LOGOUT });
    navigate("/");
  }, [dispatch, navigate]);

  useEffect(() => {
    tokenExpired(user, logout, navigate);
    setUser((user) => user);
  }, [location, user, navigate, logout]);

  const me = user?.value?.[0];
  const menu = useMemo(() => (me ? buildMenu(me, query) : []), [me, query]);

  const groupActive = useCallback(
    (item) => item.children.some((c) => !c.external && isUnder(location.pathname, c.to)),
    [location.pathname]
  );

  // Opening the sidebar expands the group of the page you're on.
  useEffect(() => {
    if (!sidebar) return;
    const current = menu
      .flatMap((s) => s.items)
      .find((item) => item.children && groupActive(item));
    if (current) setOpenGroups((g) => ({ ...g, [current.label]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebar]);

  // Esc closes the sidebar.
  useEffect(() => {
    if (!sidebar) return;
    const onKey = (e) => e.key === "Escape" && setSidebar(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebar]);

  const close = () => setSidebar(false);
  const toggleGroup = (label) => setOpenGroups((g) => ({ ...g, [label]: !g[label] }));

  if (!user) {
    return (
      <div className="mb-10">
        <UNAUTHORIZED />
      </div>
    );
  }

  const initial = (me?.username || "?").charAt(0).toUpperCase();

  return (
    <div className="mb-10">
      {/* Top bar */}
      <header className="app-topbar relative z-40 h-16 flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setSidebar(true)}
            aria-label="Open menu"
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xl
                       hover:bg-white/10 transition-colors"
          >
            <FaIcons.FaBars />
          </button>
          <span className="hidden sm:block text-white font-bold tracking-wide truncate">
            American Academy
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white/10 ring-1 ring-white/10">
            <span className="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
              {initial}
            </span>
            <span className="text-sm text-white font-semibold">{me?.username}</span>
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white
                       bg-red-600 hover:bg-red-700 active:scale-95 transition"
          >
            <FaIcons.FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </header>

      {/* Backdrop */}
      <div
        onClick={close}
        className={`fixed inset-0 z-[9998] bg-black/50 backdrop-blur-[1px] transition-opacity duration-300 ${
          sidebar ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Drawer */}
      <nav
        aria-label="Main menu"
        className={`app-drawer fixed top-0 left-0 z-[9999] h-full w-72 max-w-[85vw] flex flex-col
                    transition-transform duration-300 ease-out ${sidebar ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="flex items-center justify-between px-4 h-16 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-lg bg-blue-600 text-white font-extrabold text-sm flex items-center justify-center">
              AA
            </span>
            <span className="text-white font-bold truncate">American Academy</span>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close menu"
            className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/10"
          >
            <AiIcons.AiOutlineClose />
          </button>
        </div>

        <div className="px-4 pt-4 pb-2 shrink-0">
          <div className="relative">
            <FaIcons.FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search menu..."
              className="w-full rounded-lg bg-white/5 border border-white/10 pl-8 pr-3 py-2 text-sm text-white
                         placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="app-drawer-scroll flex-1 overflow-y-auto px-3 pb-4">
          {menu.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">No menu items match.</p>
          )}
          {menu.map((section) => (
            <div key={section.section} className="mt-3">
              <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {section.section}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) =>
                  item.children ? (
                    <MenuGroup
                      key={item.label}
                      item={item}
                      // While searching, matching groups are shown expanded.
                      open={Boolean(query.trim()) || Boolean(openGroups[item.label])}
                      onToggle={() => toggleGroup(item.label)}
                      active={groupActive(item)}
                      onNavigate={close}
                    />
                  ) : (
                    <MenuLink key={item.to} item={item} onNavigate={close} />
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="shrink-0 border-t border-white/10 px-4 py-3 flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
            {initial}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-white font-semibold truncate">{me?.username}</p>
            <p className="text-[11px] text-slate-400 capitalize">{me?.role}</p>
          </div>
          <button
            type="button"
            onClick={logout}
            title="Logout"
            aria-label="Logout"
            className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-300 hover:text-white hover:bg-red-600"
          >
            <FaIcons.FaSignOutAlt />
          </button>
        </div>
      </nav>

      <main className="py-5">
        <div>
          <Outlet />
        </div>
      </main>
      <div className="footer-style">
        <p>American Academy &copy; V4.0  ^_^</p>
      </div>
    </div>
  );
}

export default Sidebar;
